import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { t, pick } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Plus, Trash2, NotebookPen, Sparkles, FileDown, Loader2 } from "lucide-react";
import jsPDF from "jspdf";
import PaywallDialog from "@/components/PaywallDialog";
import UsageMeter from "@/components/UsageMeter";
import { useUsage } from "@/context/UsageContext";

const empty = { context: "family", observation: "", feeling: "", need: "", request: "", notes: "" };

export default function Journal() {
  const { lang } = useLang();
  const { refresh: refreshUsage } = useUsage();
  const [entries, setEntries] = useState([]);
  const [contexts, setContexts] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [refining, setRefining] = useState(false);
  const [suggestion, setSuggestion] = useState(null); // { entry_id, observation, feeling, need, request, message }
  const [paywall, setPaywall] = useState(false);
  const [analysis, setAnalysis] = useState({}); // { entry_id: {emotions, needs, reformulation, exercise_hint} }
  const [analyzingId, setAnalyzingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/journal");
      setEntries(r.data);
    } catch (e) {
      console.error("journal load failed", e);
    }
  }, []);

  const analyze = async (entry) => {
    setAnalyzingId(entry.id);
    try {
      const full = [entry.observation, entry.feeling, entry.need, entry.request, entry.notes].filter(Boolean).join(". ");
      const { data } = await api.post("/ai/analyze-journal", { text: full, language: lang });
      setAnalysis((a) => ({ ...a, [entry.id]: data }));
      refreshUsage();
    } catch (err) {
      if (err?.response?.status === 402) {
        setPaywall(true);
      } else {
        toast.error(err?.response?.data?.detail || t(lang, "error"));
      }
    } finally {
      setAnalyzingId(null);
    }
  };

  useEffect(() => {
    load();
    api.get("/content/contexts").then((r) => setContexts(r.data)).catch((e) => console.error("contexts fetch failed", e));
  }, [load]);

  const save = async () => {
    if (!form.observation || !form.feeling || !form.need || !form.request) {
      toast.error(t(lang, "error"));
      return;
    }
    setBusy(true);
    try {
      await api.post("/journal", form);
      toast.success(t(lang, "saved"));
      toast.success(t(lang, "points_earned").replace("{n}", "10"), { duration: 1800 });
      setForm(empty);
      setCreating(false);
      load();
    } catch (e) {
      console.warn("journal save failed", e?.message || e);
      toast.error(t(lang, "error"));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/journal/${id}`);
    toast.success(t(lang, "deleted"));
    load();
  };

  const refine = async (entry) => {
    setRefining(true);
    setSuggestion({ loading: true, entry_id: entry.id });
    try {
      const { data } = await api.post("/ai/rewrite-journal", {
        context: entry.context,
        observation: entry.observation,
        feeling: entry.feeling,
        need: entry.need,
        request: entry.request,
        notes: entry.notes || "",
        language: lang,
      });
      setSuggestion({ entry_id: entry.id, ...data });
      refreshUsage();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 402 && detail?.upgrade) {
        setSuggestion(null);
        setPaywall(true);
      } else {
        toast.error(t(lang, "error"));
        setSuggestion(null);
      }
    } finally {
      setRefining(false);
    }
  };

  const ctxName = (id) => {
    const c = contexts.find((x) => x.id === id);
    return c ? pick(c.name, lang) : id;
  };

  const exportPdf = (entry) => {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const margin = 48;
    const pageWidth = doc.internal.pageSize.getWidth();
    const maxWidth = pageWidth - margin * 2;
    let y = margin;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.text(`NVC Journal — ${ctxName(entry.context)}`, margin, y);
    y += 24;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(120);
    doc.text(new Date(entry.created_at).toLocaleString(), margin, y);
    y += 24;

    const sections = [
      [t(lang, "observation"), entry.observation],
      [t(lang, "feeling"), entry.feeling],
      [t(lang, "need"), entry.need],
      [t(lang, "request"), entry.request],
    ];
    if (entry.notes) sections.push([t(lang, "notes"), entry.notes]);

    sections.forEach(([label, value]) => {
      doc.setTextColor(60);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.text(label.toUpperCase(), margin, y);
      y += 16;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(12);
      doc.setTextColor(20);
      const lines = doc.splitTextToSize(value || "-", maxWidth);
      doc.text(lines, margin, y);
      y += lines.length * 16 + 14;

      if (y > 780) {
        doc.addPage();
        y = margin;
      }
    });

    doc.save(`journal-${entry.id.slice(0, 8)}.pdf`);
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-accent font-medium">{t(lang, "journal")}</div>
          <h1 className="font-heading text-4xl font-light mt-2">{t(lang, "journal")}</h1>
        </div>
        <div className="flex items-center gap-3">
          <UsageMeter />
          <Button
            data-testid="journal-month-pdf"
            variant="outline"
            className="rounded-full gap-2"
            onClick={async () => {
              const m = new Date().toISOString().slice(0, 7);
              try {
                const res = await api.get(`/journal/export?month=${m}`, { responseType: "blob" });
                const url = URL.createObjectURL(res.data);
                const a = document.createElement("a");
                a.href = url; a.download = `nvc-journal-${m}.pdf`;
                document.body.appendChild(a); a.click(); a.remove();
                URL.revokeObjectURL(url);
              } catch (err) {
                if (err?.response?.status === 402) {
                  toast.error(t(lang, "upgrade_to_unlock"));
                } else {
                  toast.error(t(lang, "error"));
                }
              }
            }}
          >
            <FileDown className="w-4 h-4" /> PDF
          </Button>
          {!creating && (
            <Button data-testid="journal-new-btn" onClick={() => setCreating(true)} className="rounded-full gap-2">
              <Plus className="w-4 h-4" /> {t(lang, "new_entry")}
            </Button>
          )}
        </div>
      </div>

      {creating && (
        <div className="p-6 md:p-8 rounded-2xl border border-border bg-card space-y-5" data-testid="journal-form">
          <div className="space-y-2">
            <Label>{t(lang, "context")}</Label>
            <Select value={form.context} onValueChange={(v) => setForm({ ...form, context: v })}>
              <SelectTrigger data-testid="journal-context"><SelectValue /></SelectTrigger>
              <SelectContent>
                {contexts.map((c) => <SelectItem key={c.id} value={c.id}>{pick(c.name, lang)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {["observation", "feeling", "need", "request"].map((key) => (
            <div key={key} className="space-y-2">
              <Label className="font-heading">{t(lang, key)}</Label>
              <Textarea
                data-testid={`journal-${key}`}
                value={form[key]}
                onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                className="p-4 min-h-[90px]"
              />
            </div>
          ))}
          <div className="space-y-2">
            <Label>{t(lang, "notes")}</Label>
            <Textarea
              data-testid="journal-notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="p-4"
            />
          </div>
          <div className="flex gap-3">
            <Button data-testid="journal-save" onClick={save} disabled={busy} className="rounded-full">{t(lang, "save")}</Button>
            <Button variant="ghost" onClick={() => { setCreating(false); setForm(empty); }} className="rounded-full">{t(lang, "cancel")}</Button>
          </div>
        </div>
      )}

      {entries.length === 0 && !creating && (
        <div className="p-12 rounded-2xl border border-dashed border-border text-center">
          <NotebookPen className="w-8 h-8 mx-auto text-muted-foreground" strokeWidth={1.5} />
          <p className="mt-4 text-muted-foreground">{t(lang, "no_entries")}</p>
        </div>
      )}

      <div className="space-y-4">
        {entries.map((e) => (
          <div key={e.id} className="p-6 rounded-xl border border-border bg-card" data-testid={`journal-entry-${e.id}`}>
            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
              <div className="flex items-center gap-3 text-sm">
                <span className="px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-xs">{ctxName(e.context)}</span>
                <span className="text-muted-foreground">{new Date(e.created_at).toLocaleString()}</span>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" onClick={() => refine(e)} disabled={refining} className="gap-2" data-testid={`journal-refine-${e.id}`}>
                  {refining && suggestion?.entry_id === e.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  {t(lang, "refine_ai")}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => exportPdf(e)} className="gap-2" data-testid={`journal-pdf-${e.id}`}>
                  <FileDown className="w-4 h-4" /> PDF
                </Button>
                <Button variant="ghost" size="sm" onClick={() => analyze(e)} disabled={analyzingId === e.id} className="gap-2" data-testid={`journal-analyze-${e.id}`}>
                  {analyzingId === e.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  Emotions
                </Button>
                <Button variant="ghost" size="icon" onClick={() => remove(e.id)} data-testid={`journal-delete-${e.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {["observation", "feeling", "need", "request"].map((k) => (
                <div key={k}>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">{t(lang, k)}</div>
                  <div className="text-base leading-relaxed">{e[k]}</div>
                </div>
              ))}
            </div>
            {e.notes && (
              <div className="mt-4 pt-4 border-t border-border">
                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">{t(lang, "notes")}</div>
                <div className="text-sm text-muted-foreground">{e.notes}</div>
              </div>
            )}
            {analysis[e.id] && (
              <div className="mt-4 pt-4 border-t border-accent/30 space-y-3" data-testid={`journal-analysis-${e.id}`}>
                <div className="text-xs uppercase tracking-widest text-accent font-medium">🐒 Bonobo analysis</div>
                {analysis[e.id].emotions?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {analysis[e.id].emotions.map((em, i) => (
                      <span key={`em-${i}-${em.name}`} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-rose-100 text-rose-800">
                        <span>{em.emoji}</span><span>{em.name}</span>
                      </span>
                    ))}
                  </div>
                )}
                {analysis[e.id].needs?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {analysis[e.id].needs.map((n, i) => (
                      <span key={`nd-${i}-${n.name}`} className="text-xs px-2.5 py-1 rounded-full bg-teal-100 text-teal-800">{n.name}</span>
                    ))}
                  </div>
                )}
                {analysis[e.id].reformulation && (
                  <div className="text-sm leading-relaxed italic bg-secondary/40 p-3 rounded-lg" data-testid={`journal-analysis-reformulation-${e.id}`}>
                    {analysis[e.id].reformulation}
                  </div>
                )}
                {analysis[e.id].exercise_hint && (
                  <div className="text-xs text-accent" data-testid={`journal-analysis-hint-${e.id}`}>
                    💡 {analysis[e.id].exercise_hint}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* AI suggestion dialog */}
      <Dialog open={!!suggestion} onOpenChange={(open) => !open && setSuggestion(null)}>
        <DialogContent className="max-w-2xl" data-testid="refine-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-accent" /> {t(lang, "ai_suggestion")}
            </DialogTitle>
            <DialogDescription>
              {lang === "es" ? "Sugerencia CNV reescrita por IA basada en tu entrada del diario."
                : lang === "it" ? "Suggerimento CNV riscritto dall'IA basato sulla tua voce di diario."
                : "AI-refined NVC suggestion based on your journal entry."}
            </DialogDescription>
          </DialogHeader>
          {suggestion?.loading ? (
            <div className="py-12 flex flex-col items-center gap-3 text-muted-foreground">
              <Loader2 className="w-8 h-8 animate-spin" />
              <p>{t(lang, "ai_rewriting")}</p>
            </div>
          ) : suggestion ? (
            <div className="space-y-4">
              {suggestion.message && (
                <div className="p-4 rounded-lg bg-accent/10 border border-accent/30">
                  <div className="text-xs uppercase tracking-widest text-accent font-medium mb-2">Message</div>
                  <p className="leading-relaxed" data-testid="refine-message">{suggestion.message}</p>
                </div>
              )}
              <div className="grid md:grid-cols-2 gap-4">
                {["observation", "feeling", "need", "request"].map((k) => (
                  suggestion[k] && (
                    <div key={k}>
                      <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">{t(lang, k)}</div>
                      <div className="text-sm leading-relaxed">{suggestion[k]}</div>
                    </div>
                  )
                ))}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <PaywallDialog open={paywall} onOpenChange={setPaywall} limitKey="journal_rewrite" page="journal" />
    </div>
  );
}
